// externals
import { RSAA } from 'redux-api-middleware';

// libraries
import type { SimpleFSA } from '@makemydeal/dr-browser-utils';
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { BffApiUrlResult } from '@makemydeal/dr-shared-ui-utils';
import { apiConfigHelper, locHrefUtil, offerSessionUtils, getDocumentReferrer } from '@makemydeal/dr-shared-ui-utils';
import { getDealXgVersionPin } from '@makemydeal/dr-sales-checkin-ui-utils';

// types
import { bootstrapActionTypes } from '@makemydeal/dr-shared-store';

export type BootstrapAppAction = SimpleFSA<typeof bootstrapActionTypes.BOOTSTRAP_REQUEST>;

export type BootstrapInitialLoadAction = SimpleFSA<typeof bootstrapActionTypes.BOOTSTRAP_INITIAL_LOAD>;

export const initialLoad = (): BootstrapInitialLoadAction => ({ type: bootstrapActionTypes.BOOTSTRAP_INITIAL_LOAD });

export const fetchBootstrapData = (appPrefix: string) => {
    return (dispatch: any) => {
        dispatch(fetchBootstrapDataWorker(appPrefix));
    };
};

export const fetchBootstrapDataWorker = (appPrefix: string) => {
    const locHref = locHrefUtil.getLocHref();
    const dealXgId = offerSessionUtils.getDealXgId();
    // Deal version persisted from the URL (persistSearchParams). When present, load that specific
    // version so the deal + trade-ins reflect the opened version rather than the latest. Scoped to
    // dealXgId so a version pinned for a previously viewed deal is never applied to a different one.
    const dealXgVersion = getDealXgVersionPin(dealXgId) || undefined;

    // get the endppoint from the domain called
    const bffApiUrlResult: BffApiUrlResult = apiConfigHelper.buildBffApiUrl(appPrefix, locHref, 'init');
    const endpoint = bffApiUrlResult.url;

    if (bffApiUrlResult.environmentName !== bffApiUrlResult.defaultEnvironmentName) {
        /* eslint-disable-next-line no-console */
        console.log(
            'OVERRIDE APPLIED: ' +
                `Environment ${bffApiUrlResult.defaultEnvironmentName} --> ${bffApiUrlResult.overriddenEnvironmentName}`
        );
    }

    const environmentType = offerSessionUtils.identifyEnvironment(bffApiUrlResult.environmentName);
    offerSessionUtils.setEnvironmentType(environmentType);

    const referrer = getDocumentReferrer();

    let requestEndpoint = endpoint;
    if (dealXgId) {
        requestEndpoint = `${endpoint}?dealXgId=${dealXgId}`;
        requestEndpoint = dealXgId && dealXgVersion ? `${requestEndpoint}&dealXgVersion=${dealXgVersion}` : requestEndpoint;
    }

    const request: RSAACallRequest = {
        endpoint: requestEndpoint,
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'x-dr-referrer': referrer },
        types: [
            bootstrapActionTypes.BOOTSTRAP_REQUEST,
            bootstrapActionTypes.BOOTSTRAP_SUCCESS,
            {
                type: bootstrapActionTypes.BOOTSTRAP_FAILURE,
                meta:
                    // this is RSAA standard code; we'll assert this coverage behavior in the reducer.
                    // istanbul ignore next
                    (_action: any, _state: any, res: any) => {
                        if (res) {
                            return {
                                status: res.status,
                                statusText: res.statusText
                            };
                        } else {
                            return {
                                statusText: 'Network request failed'
                            };
                        }
                    }
            }
        ]
    };

    return {
        [RSAA]: request
    } as RSAACallAction;
};
